import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Cell, PieChart, Pie
} from 'recharts';
import { TrendingUp, TrendingDown, Activity, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { LoadingState, useToast } from '../ui';
import useAnalyticsStore from '../../stores/analyticsStore';
import { getOperationsOverview, getPerformanceTrends } from '../../services/analyticsService';

const OperationsAnalyticsTab = () => {
  const { error: showError } = useToast();
  const { appliedFilters, dateRange } = useAnalyticsStore();

  // Build query params from filters
  const buildParams = () => {
    const params = { days: dateRange };
    if (appliedFilters.operations?.length > 0) {
      params.operations = appliedFilters.operations.join(',');
    }
    if (appliedFilters.suppliers?.length > 0) {
      params.suppliers = appliedFilters.suppliers.join(',');
    }
    return params;
  };

  // Fetch operations overview
  const { data: overview, isLoading: overviewLoading, error: overviewError } = useQuery({
    queryKey: ['operations-overview', buildParams()],
    queryFn: () => getOperationsOverview(buildParams()),
    staleTime: 60000 // 1 minute
  });

  // Fetch performance trends
  const { data: trends, isLoading: trendsLoading } = useQuery({
    queryKey: ['performance-trends', dateRange],
    queryFn: () => getPerformanceTrends({ days: dateRange, interval: 'daily' }),
    staleTime: 60000
  });

  useEffect(() => {
    if (overviewError) {
      showError('Failed to load operations analytics: ' + overviewError.message);
    }
  }, [overviewError]);

  if (overviewLoading || trendsLoading) {
    return <LoadingState message="Loading operations analytics..." />;
  }

  if (!overview) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No data available for the selected period</p>
      </div>
    );
  }

  // Prepare chart data
  const stagePerformanceData = overview.stage_performance || [];
  const dailyThroughputData = overview.throughput?.daily?.map(d => ({
    date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    total: d.count,
    completed: d.completed,
    in_progress: d.in_progress,
    rejected: d.rejected
  })) || [];

  const hourlyData = Object.entries(overview.throughput?.hourly_distribution || {}).map(([hour, count]) => ({
    hour,
    trucks: count
  }));

  const efficiency = overview.efficiency || {};

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard
          title="Total Workflows"
          value={efficiency.total_processed || 0}
          icon={Activity}
          color="blue"
        />
        <KPICard
          title="Completion Rate"
          value={`${efficiency.completion_rate || 0}%`}
          icon={CheckCircle}
          color="green"
          trend={efficiency.completion_rate >= 80 ? 'up' : 'down'}
        />
        <KPICard
          title="SLA Compliance"
          value={`${efficiency.sla_compliance_rate || 0}%`}
          icon={Clock}
          color="purple"
          trend={efficiency.sla_compliance_rate >= 90 ? 'up' : 'down'}
        />
        <KPICard
          title="Avg Daily Throughput"
          value={overview.throughput?.avg_per_day?.toFixed(1) || 0}
          icon={TrendingUp}
          color="orange"
        />
      </div>

      {/* Daily Throughput Trend */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Throughput Trend</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={dailyThroughputData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="completed" stackId="1" stroke="#10B981" fill="#10B981" name="Completed" />
            <Area type="monotone" dataKey="in_progress" stackId="1" stroke="#F59E0B" fill="#F59E0B" name="In Progress" />
            <Area type="monotone" dataKey="rejected" stackId="1" stroke="#EF4444" fill="#EF4444" name="Rejected" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Stage Performance & Hourly Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stage Performance */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Stage Performance (Avg Duration)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stagePerformanceData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" label={{ value: 'Minutes', position: 'insideBottom', offset: -5 }} />
              <YAxis type="category" dataKey="stage" width={150} />
              <Tooltip />
              <Bar dataKey="avg_duration_minutes" fill="#3B82F6">
                {stagePerformanceData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Hourly Distribution */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Hourly Truck Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={hourlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="trucks" fill="#8B5CF6" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Cycle Time Analysis */}
      {overview.cycle_times && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Cycle Time Analysis</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-600 font-medium">Average</p>
              <p className="text-2xl font-bold text-blue-900">{overview.cycle_times.avg_cycle_time_hours?.toFixed(1)}h</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-600 font-medium">Minimum</p>
              <p className="text-2xl font-bold text-green-900">{overview.cycle_times.min_cycle_time_hours?.toFixed(1)}h</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-sm text-red-600 font-medium">Maximum</p>
              <p className="text-2xl font-bold text-red-900">{overview.cycle_times.max_cycle_time_hours?.toFixed(1)}h</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-purple-600 font-medium">Sample Count</p>
              <p className="text-2xl font-bold text-purple-900">{overview.cycle_times.sample_count}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// KPI Card Component
const KPICard = ({ title, value, icon: Icon, color, trend }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600'
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div className="flex items-end justify-between">
        <p className="text-3xl font-bold text-gray-900">{value}</p>
        {trend && (
          <div className="flex items-center">
            {trend === 'up' ? (
              <TrendingUp className="h-5 w-5 text-green-500" />
            ) : (
              <TrendingDown className="h-5 w-5 text-red-500" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OperationsAnalyticsTab;
